import React, { useCallback, useMemo } from "react";

import useFormInstance from "antd/es/form/hooks/useFormInstance";

import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import User from "@mapmycustomers/shared/types/User";
import MapFilterModel, {
  ModifiedMapFilterModel,
} from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";

import MapFilterButton from "@app/component/filtering/components/MapFilterButton";
import hasIncompleteValue from "@app/component/filtering/components/MapFilterButton/utils/hasIncompleteValue";
import IMapViewContext from "@app/component/view/types/IMapViewContext";
import BaseMapViewContext from "@app/component/view/utils/BaseMapViewContext";
import CardType from "@app/enum/dashboard/CardType";
import { MapFetcherPayload } from "@app/scene/map/types/MapFetcher";
import mergeFilterModels from "@app/scene/map/utils/mergeFilterModels";
import convertMapFilterToPlatformFilterModel from "@app/util/viewModel/convertMapFilterToPlatformFilterModel";
import convertMapPlatformFilterToMapFilterModel from "@app/util/viewModel/convertMapPlatformFilterToMapFilterModel";

import styles from "./CriteriaExtraMapFilters.module.scss";

interface Props {
  cardType: CardType;
  entityTypes: EntityTypesSupportedByMapsPage[];
  hiddenFields?: IField[];
  onChange?: (filter: PlatformFilterModel) => void;
  value?: PlatformFilterModel;
}

const CriteriaExtraMapFilters: React.FC<Props> = ({
  cardType,
  entityTypes,
  hiddenFields,
  onChange,
  value,
}) => {
  const form = useFormInstance();

  const [modifiedFilterModel, setModifiedFilterModel] = useStateWithDebouncedListener(
    [
      useDynamicCallback((modifiedFilterModel: MapFilterModel) => {
        if (!hasIncompleteValue(modifiedFilterModel) && modifiedFilterModel) {
          const platformModel = convertMapFilterToPlatformFilterModel(
            modifiedFilterModel,
            entityTypes,
            undefined
          );
          onChange?.(platformModel);
        }
      }),
      500,
    ],
    (value ? convertMapPlatformFilterToMapFilterModel(value) : {}) as ModifiedMapFilterModel
  );

  const handleChange = useCallback(
    (payload: MapFetcherPayload) => {
      if (payload.request.filter) {
        setModifiedFilterModel(payload.request.filter);
      }
      if (payload.request.visibleEntities) {
        form.setFieldValue(["criteria", "entityTypes"], payload.request.visibleEntities);
      }
    },
    [form, setModifiedFilterModel]
  );

  const viewState = useMemo(() => {
    return {
      columns: [],
      filter: modifiedFilterModel as MapFilterModel,
      range: { endRow: 0, startRow: 0 },
      sidebarVisible: false,
      sort: [],
      viewAs: undefined,
      visibleEntities: entityTypes,
    };
  }, [entityTypes, modifiedFilterModel]);

  const handleUpdateViewState: IMapViewContext["updateViewState"] = useCallback(
    (request: Partial<MapViewState>) => {
      handleChange({ request, updateOnly: true });
    },
    [handleChange]
  );

  const handleUpdateViewStateAndFetch: IMapViewContext["updateViewStateAndFetch"] = useCallback(
    (payload: MapFetcherPayload) => handleChange({ request: payload.request }),
    [handleChange]
  );

  const handleUpdateViewAs: IMapViewContext["updateViewAs"] = useDynamicCallback(
    (userId: undefined | User["id"]) => {
      handleUpdateViewState({ viewAs: userId });
    }
  );

  const handleUpdateFilterModel: IMapViewContext["updateFilterModel"] = useDynamicCallback(
    (newFilterModel: ModifiedMapFilterModel) => {
      let resultingFilterModel = mergeFilterModels(viewState.filter, newFilterModel);
      if (resultingFilterModel.universal === undefined) {
        const { universal } = viewState.filter;
        const updatedUniversalFilter = universal?.territories
          ? { territories: universal.territories }
          : undefined;
        resultingFilterModel = { ...resultingFilterModel, universal: updatedUniversalFilter };
      }
      handleChange({ request: { filter: resultingFilterModel } });
    }
  );

  const context: IMapViewContext = useMemo(
    () => ({
      updateFilterModel: handleUpdateFilterModel,
      updateViewAs: handleUpdateViewAs,
      updateViewState: handleUpdateViewState,
      updateViewStateAndFetch: handleUpdateViewStateAndFetch,
      viewState,
    }),
    [
      handleUpdateFilterModel,
      handleUpdateViewAs,
      handleUpdateViewState,
      handleUpdateViewStateAndFetch,
      viewState,
    ]
  );

  return (
    <section className={styles.container}>
      <BaseMapViewContext.Provider
        // Ignored because TS can't be sure that the Provider was created for exactly same
        // entity type as T. Although we know that it will be.
        // @ts-ignore
        value={context}
      >
        <MapFilterButton
          context={`dashboard/${cardType}`}
          hiddenFields={hiddenFields}
          hideSavedFilters
          // To put filter popover above dashboard cards edit modal
          zIndex={1000}
        />
      </BaseMapViewContext.Provider>
    </section>
  );
};

export default CriteriaExtraMapFilters;
